import Vue from "vue";
import i18n from "../../i18n";
import permissions from "../permissions/organization";
import { asyncLocalStorage } from "@Helpers/support";
import { defaultPeriod } from "@Constants/Periods";
import FlattenObjectValues from "@Helpers/functions/flattenObjectValues";

import useNotify from "@Composables/useNotify";

export default {
    modules: { permissions },

    state: {
        isAnalyticsOpen: false,
        areTinyurlsLoading: false,

        reonboardingStep: undefined,
        organization: undefined,
        organizationDomains: undefined,
        organizationRoles: undefined,
        organizationUsers: undefined,
        organizationTeam: undefined,

        // tinyurls filters
        tinyurlsFilters: {
            search: {
                query: "",
                type: "alias",
            },
            state: {
                name: i18n.t("urls.filter.available"),
                icon: "fa-globe",
                action: "available",
            },
        },

        // dashboard filters
        filters: {
            member: null,
            period: defaultPeriod,
        },

        tinyurls: { data: [] },
        tinyurl: {},
    },

    getters: {
        isAnalyticsOpen: (state, getters) => state.isAnalyticsOpen,
        areTinyurlsLoading: (state, getters) => state.areTinyurlsLoading,

        reonboardingStep: (state, getters) => state.reonboardingStep || 0,
        organization: (state, getters) => state.organization,
        organizationDomains: (state, getters) => state.organizationDomains || [],
        organizationRoles: (state, getters) => state.organizationRoles || [],
        organizationTeams: (state, getters) => {
            let teams = state.organization?.teams;

            // attach team status if not defined for later use
            teams = teams?.map((t) => {
                if ("status" in t) {
                    return t;
                } else {
                    return {
                        ...t,
                        status: { archived: false },
                    };
                }
            });

            // do not return archived teams
            teams = teams?.filter((t) => {
                if ("status" in t) {
                    if (t.status.archived == false) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            });

            // sorted by team name alphabetically
            teams = teams?.sort((a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();

                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;

                return 0;
            });

            return teams;
        },
        organizationUsers: (state, getters) => state.organizationUsers || [],
        organizationTeam: (state, getters) => state.organizationTeam,

        tinyurlsFilters: (state, getters) => state.tinyurlsFilters,
        filters: (state, getters) => state.filters,

        tinyurls: (state, getters) => state.tinyurls,
        tinyurl: (state, getters) => state.tinyurl,
        tinyurlIndex: (state, getters) => {
            return getters.tinyurls.data.findIndex((t) => {
                const alias = getters.tinyurl.aliases.find((a) => a.is_main);

                return t.aliases.some(
                    (a) => a.alias === alias.alias && a.domain === alias.domain,
                );
            });
        },
    },

    mutations: {
        TOGGLE_ANALYTICS: (state, payload) => {
            if (payload !== undefined) state.isAnalyticsOpen = payload;
            else state.isAnalyticsOpen = !state.isAnalyticsOpen;
        },
        TOGGLE_TINYURLS: (state, payload) => {
            state.areTinyurlsLoading = payload;
        },
        SET_ORGANIZATION: (state, payload) => {
            state.organization = payload;
        },
        SET_ORGANIZATION_DOMAINS: (state, payload) => {
            state.organizationDomains = payload;
        },
        SET_ORGANIZATION_ROLES: (state, payload) => {
            state.organizationRoles = payload;
        },
        SET_ORGANIZATION_TEAMS: (state, payload) => {
            state.organization = {
                ...state.organization,
                teams: payload,
            };
        },
        SET_ORGANIZATION_TEAM: (state, payload) => {
            state.organizationTeam = payload;
        },
        SET_ORGANIZATION_USERS: (state, payload) => {
            state.organizationUsers = payload;
        },
        CLEAR_TINYURLS: (state) => {
            state.tinyurls = { data: [] };
        },
        REMOVE_TINYURL: (state, payload) => {
            const remaining = state.tinyurls.data.filter((t) => {
                const alias = t.aliases.find((a) => a.is_main);
                return alias.alias != payload.aliases.find((a) => a.is_main).alias;
            });

            state.tinyurls = {
                ...state.tinyurls,
                data: remaining,
            };
        },
        SET_TINYURLS_FILTERS_SEARCH: (state, payload) => {
            state.tinyurlsFilters = {
                ...state.tinyurlsFilters,
                search: payload,
            };
        },
        SET_TINYURLS_FILTERS_STATE: (state, payload) => {
            state.tinyurlsFilters = {
                ...state.tinyurlsFilters,
                state: payload,
            };
        },
        SET_TINYURLS: (state, payload) => {
            state.tinyurls = payload;
        },
        SET_TINYURL: (state, payload) => {
            state.tinyurl = payload;
        },
        PUSH_TINYURL: (state, payload) => {
            state.tinyurls = {
                ...state.tinyurls,
                data: [payload, ...state.tinyurls.data],
            };
        },
        PUSH_TEAM: (state, payload) => {
            state.organization = {
                ...state.organization,
                teams: [
                    ...state.organization.teams.filter((t) => t.slug != payload.slug),
                    payload,
                ],
            };
        },
        UPDATE_TEAM: (state, updated) => {
            state.organization = {
                ...state.organization,
                teams: state.organization.teams.map((team) => {
                    if (team.slug != updated.slug) return team;
                    return { ...team, ...updated };
                }),
            };
        },
        UPDATE_DOMAIN: (state, updated) => {
            state.organizationDomains = state.organizationDomains.map((domain) => {
                if (domain.domain != updated.domain) return domain;
                return updated;
            });
        },
        UPDATE_FILTERS: (state, payload) => {
            state.filters = {
                ...state.filters,
                ...payload,
            };
        },
        UPDATE_REONBOARDING_STEP: (state, payload) => {
            state.reonboardingStep = payload;
        },
        UPDATE_TINYURL: (state, payload) => {
            const { index, tinyurl } = payload;

            if (index === undefined || !_.has(state.tinyurls.data, index)) {
                return;
            }

            _.forEach(tinyurl, (value, key) => {
                Vue.set(state.tinyurls.data[index], key, value);
            });
        },
        UPDATE_USER: (state, updated) => {
            state.organizationUsers = state.organizationUsers.map((user) => {
                if (user.email != updated.email) return user;
                return updated;
            });
        },
    },

    actions: {
        deleteOrg({ commit, getters, state }, { params, options }) {
            const url = Vue.apiUrl("/organization/{o}", {
                o: params.org,
            });

            axios.delete(url).then(({ data }) => {
                // optional callback
                if (
                    options &&
                    "callback" in options &&
                    options.callback instanceof Function
                ) {
                    options.callback(data.data);
                }
            });
        },

        fetchOrg({ commit, getters }, payload) {
            const url = Vue.apiUrl("/organization/{o}", {
                o: payload.org,
            });

            axios.get(url).then(({ data }) => {
                const organization = data.data;
                delete organization["teams"];

                commit("SET_ORGANIZATION", {
                    ...getters.organization,
                    ...organization,
                });

                // optional callback
                if ("callback" in payload && payload.callback instanceof Function) {
                    payload.callback(data.data);
                }
            });
        },

        fetchOrgDomains({ commit, getters, state }, payload = {}) {
            const { params, options } = payload;

            if (state.organizationDomains === undefined || options?.force) {
                const url = Vue.apiUrl("/organization/{o}/domains", {
                    o: params?.org || state.organization.slug,
                });

                axios.get(url).then(({ data }) => {
                    commit("SET_ORGANIZATION_DOMAINS", data.data);

                    // optional callback
                    if (
                        options &&
                        "callback" in options &&
                        options.callback instanceof Function
                    ) {
                        options.callback(data.data);
                    }
                });
            }
        },

        fetchOrgRoles({ commit, getters, state }, { params, options }) {
            if (state.organizationRoles === undefined || options?.force) {
                const url = Vue.apiUrl("/organization/{o}/roles", {
                    o: params.org,
                });

                axios.get(url).then(({ data }) => {
                    commit("SET_ORGANIZATION_ROLES", data.data);

                    // optional callback
                    if (
                        options &&
                        "callback" in options &&
                        options.callback instanceof Function
                    ) {
                        options.callback(data.data);
                    }
                });
            }
        },

        fetchOrgUsers({ commit, getters, state }, { params, options }) {
            if (state.organizationUsers === undefined || options?.force) {
                const url = Vue.apiUrl("/organization/{o}/users", {
                    o: params.org,
                });

                axios.get(url).then(({ data }) => {
                    commit("SET_ORGANIZATION_USERS", data.data);

                    // optional callback
                    if (
                        options &&
                        "callback" in options &&
                        options.callback instanceof Function
                    ) {
                        options.callback(data.data);
                    }
                });
            }
        },

        fetchOrgTeam({ commit, getters }, payload) {
            let url = `organization/{o}/team/{t}`;
            url = Vue.apiUrl(url, { o: payload.org, t: payload.team });

            axios.get(url).then(({ data }) => {
                const teams = getters.organizationTeams;
                const current = teams.find((t) => t.slug == data.data.slug);

                // this will only update the key/value pairs that has been changed
                commit("UPDATE_TEAM", {
                    ...current,
                    ...data.data,
                });

                // optional callback
                if ("callback" in payload && payload.callback instanceof Function) {
                    payload.callback(data.data);
                }
            });
        },

        fetchTinyurls({ commit, dispatch, getters }, payload) {
            const dashboard = _.head(_.pullAt(payload, "dashboard"));
            const { notifyError } = useNotify();

            let url;

            if (dashboard == "main") {
                url = `organization/{organization}/urls`;
            } else if (dashboard == "team") {
                url = `organization/{organization}/team/{team}/urls`;
            } else {
                url = `organization/{organization}/personal/urls`;
            }

            url = `${url}/${getters.tinyurlsFilters.state.action}`;

            if (getters.tinyurlsFilters.search) {
                url = `${url}?search=${getters.tinyurlsFilters.search.type}:${getters.tinyurlsFilters.search.query}`;
            }

            axios
                .get(Vue.apiUrl(url, payload))
                .then(({ data }) => {
                    commit("SET_TINYURLS", data);

                    // optional callback
                    if ("callback" in payload && payload.callback instanceof Function) {
                        payload.callback(data);
                    }
                })
                .catch((e) => {
                    let text;

                    if (_.has(e.response.data, "errors")) {
                        text = FlattenObjectValues(e.response.data.errors).join("<br />");
                    } else {
                        text = i18n.t("something_went_wrong");
                    }

                    notifyError(text);
                    commit("TOGGLE_TINYURLS", false);
                });
        },

        fetchTinyurl({ commit }, payload) {
            let url;

            if (payload.hasOwnProperty("team")) {
                url = `organization/{organization}/team/{team}/alias/{alias}`;
            } else {
                url = `organization/{organization}/personal/alias/{alias}`;
            }

            axios
                .get(Vue.apiUrl(url, payload))
                .then(({ data }) => {
                    commit("SET_TINYURL", data.data);

                    // this will return a new promise hence the reason for another `.then` clause below
                    return Vue.nextTick().then(() => data.data);
                })
                .then((data) => {
                    // optional callback
                    if ("callback" in payload && payload.callback instanceof Function) {
                        payload.callback(data);
                    }
                });
        },

        fetchTeamMembers({ }, payload) {
            let url = `organization/{o}/team/{t}/members`;
            url = Vue.apiUrl(url, { o: payload.org, t: payload.team });

            return axios.get(url);
        },

        setupReonboarding({ commit }, { params, options }) {
            asyncLocalStorage.getItem("orgOnbStatus").then(async (data) => {
                const url = Vue.apiUrl(`organization/${params.org.slug}`);
                const status = JSON.parse(data)[params.org.slug];
                const rest = async () => {
                    if (!status.includes("team")) {
                        const team = params.org.teams[0];

                        if (!team) {
                            commit("UPDATE_REONBOARDING_STEP", 3);
                            options.callback();
                            return;
                        }
                    }
                    if (!status.includes("members")) {
                        const team = params.org.teams[0];

                        if (team) {
                            const queries = await Promise.all([
                                axios.get(`${url}/team/${team.slug}/members`),
                                axios.get(`${url}/team/${team.slug}/invitations`),
                            ]);

                            const members = queries[0].data.data;
                            const invitations = queries[1].data.data.filter((i) => {
                                return i.team?.slug == team.slug;
                            });

                            // if there are no invitations or members for the current team
                            if (!members.length && !invitations.length) {
                                commit("UPDATE_REONBOARDING_STEP", 4);
                                options.callback();
                                return;
                            }
                        }
                    }
                    commit("UPDATE_REONBOARDING_STEP", 5);
                    options.callback();
                    return;
                };

                if (params.org.owner) {
                    if (!status.includes("domains")) {
                        const { data: data1 } = await axios.get(`${url}/domains`);

                        if (!data1.data.length) {
                            commit("UPDATE_REONBOARDING_STEP", 1);
                            options.callback();
                            return;
                        }
                    }
                    if (!status.includes("users")) {
                        const { data: data1 } = await axios.get(`${url}/users`);
                        const { data: data2 } = await axios.get(`${url}/users/invitations`);

                        if (![...data1.data, ...data2.data].length) {
                            commit("UPDATE_REONBOARDING_STEP", 2);
                            options.callback();
                            return;
                        }
                    }
                    rest();
                } else {
                    rest();
                }
            });
        },

        archiveOrg({ }, { params, options }) {
            const url = Vue.apiUrl("organization/{o}/archive", {
                o: params.org,
            });

            axios.patch(url, { archive: true }).then(({ data }) => {
                // optional callback
                if (
                    options &&
                    "callback" in options &&
                    options.callback instanceof Function
                ) {
                    options.callback(data);
                }
            });
        },

        unarchiveOrg({ }, { params, options }) {
            const url = Vue.apiUrl("organization/{o}/archive", {
                o: params.org,
            });

            axios.patch(url, { archive: false }).then(({ data }) => {
                // optional callback
                if (
                    options &&
                    "callback" in options &&
                    options.callback instanceof Function
                ) {
                    options.callback(data);
                }
            });
        },
    },
};
